/**
 * Drives an infinite-scroll list to load every page before an action that needs the whole set.
 *
 * LSP-18421: the timeline renders one page at a time and only loads more when the user scrolls,
 * so "Select All" saw whichever rows happened to be on screen — on a session with 200 photos it
 * selected the first page and stopped. The true total already arrives as a prop, so we page until
 * the loaded set reaches it, then select.
 */
export interface PagedSource<TItem> {
	/** The items loaded so far. */
	getLoaded: () => readonly TItem[];
	/** How many items exist in total, server-side. */
	getTotal: () => number;
	/** Asks the owner to load the next page. */
	requestPage: () => void;
	/** Resolves once the loaded set has changed (or the wait times out). */
	waitForMore: (loadedLengthBeforeRequest: number) => Promise<void>;
	/**
	 * Hard ceiling on page requests. A count that the server can never satisfy (rows deleted
	 * since it was computed) must not turn into unbounded requests.
	 */
	maxPages?: number;
}

const DEFAULT_MAX_PAGES = 200;

export async function loadAllPages<TItem>(source: PagedSource<TItem>): Promise<readonly TItem[]> {
	const maxPages = source.maxPages ?? DEFAULT_MAX_PAGES;

	for (let requests = 0; requests < maxPages; requests += 1) {
		const loadedLength = source.getLoaded().length;
		const total = source.getTotal();

		if (!total || loadedLength >= total) {
			break;
		}

		source.requestPage();
		await source.waitForMore(loadedLength);

		// No progress means the total is unreachable — stop rather than spin.
		if (source.getLoaded().length <= loadedLength) {
			break;
		}
	}

	return source.getLoaded();
}
